import lodashMap from 'lodash/map';
import invert from 'lodash/invert';
import isFinite from 'lodash/isFinite';
import { ROOT_URL, ROOT_API_URL, isARC, isOLG, isHMM, isSH } from 'RootUrl';
import moment from 'moment';

const isEcity = (ROOT_API_URL === 'https://ct-scm.truckxi.com/api/') || (ROOT_API_URL === 'https://ctstage-scm.truckxi.com/api/');
const isMyumyu = (ROOT_API_URL === 'https://mmstage-scm.truckxi.com/api/' || ROOT_API_URL === 'https://myumyu-scm.truckxi.com/api/');
const isAFG = ROOT_API_URL === 'https://afg-scm.truckxi.com/api/' || ROOT_URL === 'https://afg-scm.truckxi.com/api/';
const isRivera = (ROOT_API_URL === 'https://rivera-scm.truckxi.com/api/' || ROOT_URL === 'https://rvrstage-scm.truckxi.com/api/');
const isWecom = ROOT_API_URL === 'https://wecom-scm.truckxi.com/api/' || ROOT_API_URL === 'https://wecom-scm.truckxi.com/api/';
const isAFM = ROOT_API_URL === 'https://afm-scm.truckxi.com/api/' || ROOT_URL === 'https://afm-scm.truckxi.com/api/';
const isUSPride = ROOT_API_URL === 'https://uspride-scm.truckxi.com/api/' || ROOT_URL === 'https://uspride-scm.truckxi.com/api/';

export function roundTo(n, digits) {
  if (digits === undefined) {
    digits = 0;
  }

  const multiplicator = Math.pow(10, digits);
  n = parseFloat((parseFloat(n) * multiplicator).toFixed(11));
  const test = (Math.round(n) / multiplicator);
  return +(test.toFixed(2));
}

export function roundToDecimals(n, decimals=2) {
  const multiplicator = Math.pow(10, decimals);
  n = parseFloat((parseFloat(n) * multiplicator).toFixed(11));
  const test = (Math.round(n) / multiplicator);
  return +(test.toFixed(decimals));
}

export const roundUp = (price) => {
  if(price == undefined || isNaN(price)) {
    return 0;
  }
  const refPrice = parseFloat(price);
  let val = parseInt(refPrice * 10, 10);
  val /= 10;
  const left = +(refPrice - val).toFixed(8);
  let rounded = 0;
  if (left === 0) {
    rounded = 0;
  } else if (left > 0 && left <= 0.05) {
    rounded = 0.05;
  } else if (left > 0.05 && left <= 0.09) {
    rounded = 0.09;
  } else {
    rounded = 0.1;
  }
  rounded += val;
  rounded = rounded.toFixed(2);
  return rounded;
};

export const normalizeFourDecimals = (value) => {
  if (value === null || value === '' || value === undefined) { return ''; }
  let v = value.toString().replace(/[^\d.]/g, '')
  v = v.slice(0, v.indexOf('.') >= 0 ? v.indexOf('.') + 5 : undefined)
  return v
}

const CALCULATION_TYPE = {
  NORMAL: 'normal',
  UNIT: 'unit',
};

export const getRoundingFunc = (type = CALCULATION_TYPE.NORMAL) => {
  if (type === CALCULATION_TYPE.UNIT) {
    return roundUp;
  }
  return roundTo;
};

export function decimalPlaces(num) {
  const match = (`${num}`).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
  if (!match) { return 0; }
  return Math.max(
    0,
    // Number of digits right of decimal point.
    (match[1] ? match[1].length : 0)
       // Adjust for scientific notation.
       - (match[2] ? +match[2] : 0),
  );
}

export function isNumber(num) {
  return !Number.isNaN(parseFloat(num)) && Number.isFinite(parseFloat(num));
}

/**
 *
 * @param {Array} lines // lines of products
 * @param {Number} discount // ex: 15
 * @param {Number} taxRate // ex: 0.03
 */
export function calculateTax(lines, discount = 0, taxRate) {
  let taxableTotal = 0;
  if (lines && lines.length > 0) {
    lodashMap(lines, (line) => {
      if (line.IsTaxable) {
        let totalPriceWithSavings = 0;
        if(isHMM) {
          totalPriceWithSavings = parseFloat(line.Quantity * line.UnitPrice * line.PackageSize_1) * ((100 - parseFloat(discount)) / 100);
        } else {
          totalPriceWithSavings = parseFloat(line.TotalPrice) * ((100 - parseFloat(discount)) / 100);
        }
        taxableTotal += roundTo(totalPriceWithSavings, 2);
      }
    });
  }
  return roundTo((taxableTotal * taxRate), 2).toFixed(2);
}

/**
 *
 * @param {Number} totalPrice
 * @param {Number} discount
 */
export function calculateSavingAmount(totalPrice, discount) {
  return roundTo(parseFloat(totalPrice) * (parseFloat(discount) / 100), 2);
}

/**
 *
 * @param {Number} discount
 * @param {Number} savings
 * @param {Number} totalPrice
 * @param {Number} totalCRV
 * @param {Number} totalTax
 */
export function calculateSavings(discount, savings, totalPrice, totalCRV, totalTax, delivery) {
  if (isMyumyu || isAFG || isRivera || isWecom || isAFM || isSH) {
    if (discount > 0) {
      return (roundTo((roundTo(parseFloat(totalPrice), 2) - calculateSavingAmount(totalPrice, discount)) + roundTo(parseFloat(totalCRV), 2) + roundTo(parseFloat(totalTax), 2), 2) + roundTo(parseFloat(delivery), 2)).toFixed(2);
    }
    return (roundTo(roundTo(parseFloat(totalPrice), 2) - roundTo(parseFloat(savings), 2), 2) + roundTo(parseFloat(totalCRV), 2) + roundTo(parseFloat(totalTax), 2) + roundTo(parseFloat(delivery), 2)).toFixed(2);
  }
  if (discount > 0) {
    return (roundTo((roundTo(parseFloat(totalPrice), 2) - calculateSavingAmount(totalPrice, discount)) + roundTo(parseFloat(totalCRV), 2) + roundTo(parseFloat(totalTax), 2), 2)).toFixed(2);
  }
  return (roundTo(roundTo(parseFloat(totalPrice), 2) - roundTo(parseFloat(savings), 2), 2) + roundTo(parseFloat(totalCRV), 2) + roundTo(parseFloat(totalTax), 2)).toFixed(2);
}

/**
 * Calculates price percentage for `Cost Margin` percentage
 * Formula: (priceLvl $ - refPrice / refPrice) * 100%
 * @param {Number} price product priceLvl price
 * @param {Number} refPrice referencing price for calculation
 * @returns Price percentage by `Cost Margin`
 */
export const calculatePricePercentage = (price, refPrice, arrivalPrice, ProductCost) => {
  let percentage = roundToDecimals((((parseFloat(price) || 0) / (parseFloat(refPrice) || 0)) * 100) - 100, 4);
  if (isEcity) {
    percentage = parseFloat(price - arrivalPrice) / parseFloat(price) * 100;
  }
  if (isHMM) {
    percentage = parseFloat(price - arrivalPrice) / parseFloat(arrivalPrice) * 100;
  }
  if (isUSPride) {
    percentage = (1 - arrivalPrice/price) * 100;
  }
  if (isARC) {
    percentage = roundToDecimals((price - ProductCost) / price * 100, 4);
  }
  return isFinite(percentage) ? percentage.toFixed(4) : 0;
};

/**
 * Calculates price percentage for `Sales Margin` percentage
 * Formula: (priceLvl $ - refPrice / priceLvl $) * 100%
 * @param {Number} price product priceLvl price
 * @param {Number} refPrice referencing price for calculation
 * @returns Price percentage by `Sales Margin`
 */
export const calculatePricePercentageSalesMargin = (price, refPrice) => {
  const percentage = roundTo((((parseFloat(price) || 0) - (parseFloat(refPrice) || 0)) / (parseFloat(price) || 0)) * 100, 3);
  return isFinite(percentage) ? percentage.toFixed(2) : 0;
};

/**
 * Calculates priceLvl price with rounding 0.0 ~ 0.5 -> 0.5, 0.6 ~ 0.9 -> 0.9
 * @param {Number} pricePercentage product price level percentage
 * @param {Number} refPrice product reference price
 */
export const calculatePriceLevelUnit = (pricePercentage, refPrice) => (
  roundTo(roundUp(((parseFloat(pricePercentage) || 0) + 100) * (parseFloat(refPrice) || 0) * 0.01), 3).toFixed(2)
);

/**
 * Calculates priceLvl price
 * @param {Number} pricePercentage product price level percentage
 * @param {Number} refPrice product reference price
 */

export const calculatePriceLevelNormal = (pricePercentage, refPrice, ArrivalPrice = 0, ProductCost = 0) => {
  // if(isHMM) {
  //   const result = parseFloat((parseFloat(ArrivalPrice) / ( 1 - parseFloat(pricePercentage)/100 )).toFixed(2));
  //   return result;
  // }
  if (isEcity || isUSPride) {
    const result = ArrivalPrice / (1 - pricePercentage/100);
    return roundToDecimals(result);
  }
  if (isHMM) {
    const result = ArrivalPrice * (1 + (pricePercentage/100));
    return roundToDecimals(result);
  }
  if (isARC) {
    return roundToDecimals(ProductCost / (1 - (pricePercentage / 100)), 2);
  }
 
  return roundToDecimals((((parseFloat(pricePercentage) || 0) + 100) * (parseFloat(refPrice) || 0) * 0.01).toFixed(4), (isHMM || isOLG) ? 2 : 4);
};

/**
 * Gives corresponding calculation function
 * @param {String} type of CALCULATION_TYPE
 * @returns calculate price level function of type
 */
export const calculatePriceLevel = (type) => {
  if (type === CALCULATION_TYPE.UNIT) {
    return calculatePriceLevelUnit;
  }
  return calculatePriceLevelNormal;
};


/**
 * Calculates priceLvl price with rounding 0.0 ~ 0.5 -> 0.5, 0.6 ~ 0.9 -> 0.9
 * @param {Number} pricePercentage product price level percentage
 * @param {Number} refPrice product reference price
 */
export const calculatePriceLevelSalesMarginUnit = (pricePercentage, refPrice) => {
  const price = roundTo(roundUp((parseFloat(refPrice) || 0) / ((100 - (parseFloat(pricePercentage) || 0)) / 100)), 3);
  return isFinite(price) ? price.toFixed(2) : 0;
};

/**
 * Calculates priceLvl price
 * @param {Number} pricePercentage product price level percentage
 * @param {Number} refPrice product reference price
 */
export const calculatePriceLevelSalesMarginNormal = (pricePercentage, refPrice) => {
  const price = roundTo(((parseFloat(refPrice) || 0) / ((100 - (parseFloat(pricePercentage) || 0)) / 100)).toFixed(2), 3);
  return isFinite(price) ? price.toFixed(2) : 0;
};

/**
 * Gives corresponding calculation function
 * @param {String} type of CALCULATION_TYPE
 * @returns calculate price level function of type
 */
export const calculatePriceLevelSalesMargin = (type) => {
  if (type === CALCULATION_TYPE.UNIT) {
    return calculatePriceLevelSalesMarginUnit;
  }
  return calculatePriceLevelSalesMarginNormal;
};

/**
 * Maps price level field name to price level percentage field name
 */
export const priceLevelFieldsMap = {
  PriceLv1: 'P1Percentage',
  PriceLv2: 'P2Percentage',
  PriceLv3: 'P3Percentage',
  PriceLv4: 'P4Percentage',
  PriceLv5: 'P5Percentage',
};

/**
 * Inverted map of priceLevelFieldsMap
 * Maps price level percentage field name to price level field name
 */
export const priceLevelPercentageFieldsMap = invert(priceLevelFieldsMap);

export const getObjectValue = (object) => {
  if (typeof object === 'object') {
    return object.value
  }
  return object;
}


export const isDst = () => {
  return moment().isDST();
}


// WEBPACK FOOTER //
// ./master/jsx/components/Common/NumberUtil.js